import {
  ControlledDropDown,
  CustomButton,
  Modal,
  OnTabChangeCallbackAttributes,
  SvgAdd,
  SvgCross,
  SvgDropdownArrowGrey,
  SvgEditBlue,
  SvgPipe,
  SvgSearchIcon,
  SvgWarning,
  TabSplits,
} from '@ui-kit'
import { LookupRendererProps } from '../types/formRenderer.types'
import { FieldValues, Path, PathValue, useWatch } from 'react-hook-form'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addToaster,
  DataTypeStore,
  getLayoutByIdApi,
  hideLoader,
  LayoutResponseType,
  showLoader,
  getGenericEntityListingAPI,
  setContextualObject,
  GetGenericEntityListingApiType,
  DropdownType,
  getLayoutByEntityIdAndTypeApi,
  SelectedField,
  FieldDataByFieldNameType,
  fetchDuplicateRecordsAPI,
  getEntityDataByEntityFilter,
  updatePatientData,
  transformToDuplicateApiPayload,
  FormRuleType,
} from '@app-store'
import { useAppDispatch, useAppSelector, RootState } from '../utils/agent.store'
import {
  ElementType,
  EntityLayout,
  FilterObject,
  IDObject,
  LayoutData,
  MacroResult,
  NestedObject,
  RelatedEntity,
  RootObject,
} from '../types/layout.types'
import { generateLabelFromSelectedFields } from '../utils/listing.utils'
import {
  addEntityData,
  editEntityData,
  getEntityData,
  getEntityDataByIdEmbedded,
} from '../apis/entityData.api'
import {
  DEDUPE_BORDER_MAP,
  DYNAMIC_QUEUE_URL_ROUTE,
  ENTITY_MAP,
  ENTITY_NAMES_RELATION_MAPPING,
  fieldKeyEntityMapping,
  fieldKeyStagingMapping,
  HYBRID_LAYOUT_CONSTANTS,
  LAYOUT_TYPE_NAMESPACE,
  MODE_CONSTANTS,
  NAMESPACES,
  PATIENT_LAYOUT_KEYWORDS,
} from '../utils/constants'
import FormRenderer from './FormRenderer'
import { AddDataModal } from './MultiLookupRowRender'
import { isEmpty, uniqueId, merge, reduce, get, keyBy } from 'lodash'
import { TOASTER_NAMESPACE } from '../constants/addEditPage.strings'
import AddEditRecordPage from '../pages/AddEditViewRecord.page'
import {
  formatDataBeforeLoad,
  getKeyNameForDisabledFieldsOfSingleLookup,
  validatePhoneNumberAndAddress,
} from '../utils/form.utils'
import {
  RECORD_STATUS,
  ShortcutKeys,
  TAB_INDEX_DEFAULT,
} from '../constants/form.constants'
import { fetchMacrosApi } from '../apis/contextual.api'
import { useLocation, useParams } from 'react-router-dom'
import { editRoute, entitiesRoute, viewRoute } from '../routes/route.constant'
import {
  DedupeFieldNameType,
  IModalState,
  ShowEditLayoutPropsType,
  ShowNoLayoutSectionPropsType,
} from '../types/types'
import usePrevious from '../hooks/usePrevious'
import {
  APPLICATION_PREFERENCES_STRINGS,
  CONFIG_STRINGS,
} from '../utils/strings'
import { AdvancedSearchInfo } from '../pages/call-interaction/RequestorSearchForm'
import { ListingTableRow } from '../types/listingTable.types'
import PotentialMatchTab from './PotentialMatchTab'
import { AdvancedSearchTab } from './AdvancedSearchTab'
import { getDedupeResult } from '../utils/helperFunctions'
import { evaluateFormula } from '../utils/formula.utils'
import { useUpdateEffect } from '../hooks/useUpdateEffect'
import {
  ADULT_PATIENT_POPUP_MESSAGE,
  MINOR_PATIENT_POPUP_MESSAGE,
  UNRESOLVED_FACILITY_POPUP_MESSAGE,
} from '../constants/formattedMessages'
import { triggerFormBehaviorEvaluation } from '../form-behavior/formBehaviorTrigger.registry'
import { QUEUE_STRINGS } from '../utils/queueIntake.types'

const LookupRender = <T extends FieldValues>({
  element,
  errors,
  control,
  setValue,
  formUniqueId = '',
  fullLayout,
  subFormMetaObject,
  mode,
  dataQaId,
  defaultValue = '',
  isCustomForm = false,
  isAssociated = false,
  associatedObject = null,
  getValues,
  behaviorLookupFilter,
  ...rest
}: LookupRendererProps<T>) => {
  const layoutUid = (fullLayout as { uid?: string } | undefined)?.uid
  const siteSettings = useAppSelector(
    (state: RootState) => state.authUser.site_settings
  )
  const hasMultiProductSetting = siteSettings?.find(
    (item) => item.key === QUEUE_STRINGS.IS_MULTI_PRODUCT_ENABLED
  )?.value

  const hasMultiProduct =
    hasMultiProductSetting === 'true' || hasMultiProductSetting === true

  const automationStagedDetails = useAppSelector(
    (state: RootState) => state?.workflowData.automationStagedDetails
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const dispatch = useAppDispatch()
  const { caseId, id: routeRecordId } = useParams<string>()
  const { pathname } = useLocation()
  const isDynamicQueue = pathname.includes(DYNAMIC_QUEUE_URL_ROUTE)
  const macroRef = useRef<FieldValues>({})
  const lastFilterFingerprintRef = useRef<string | null>(null)
  const hadCompleteRelatedFiltersRef = useRef(false)
  const hasUserChangedParentRef = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [entityData, setEntityData] = useState<DataTypeStore[] | null>(null)
  const [refetchOptions, setRefetchOptions] = useState<boolean>(false)
  const [refetchSelectedId, setRefetchSelectedId] = useState<boolean>(false)
  const isLoading = useAppSelector((state) => state.loader.loading)
  const [editLayout, setEditLayout] = useState<LayoutResponseType>()
  const [viewLayout, setViewLayout] = useState<LayoutResponseType>()

  const [createLayout, setCreateLayout] = useState<LayoutResponseType>()
  const [isCreateNew, setIsCreateNew] = useState<IModalState>({
    isOpen: false,
    isCreate: null,
  })
  const [isAdvancedSearch, setIsAdvancedSearch] = useState<boolean>(false)
  const [viewEntityRecord, setViewEntityRecord] = useState<
    DataTypeStore | undefined
  >()
  const [editEntityRecord, setEditEntityRecord] = useState<boolean | undefined>(
    false
  )
  const [addEntityRecord, setAddEntityRecord] = useState<boolean | undefined>(
    false
  )
  const [advancedFiltersData, setAdvancedFiltersData] =
    useState<AdvancedSearchInfo>()
  const [macrosData, setMacrosData] = useState<MacroResult[]>()
  const [editRecordData, setEditRecordData] = useState<FieldValues>()
  const [filteredData, setFilteredData] = useState<FieldValues[]>()
  const [options, setOptions] = useState<DropdownType[]>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedOptionData, setSelectedOptionData] =
    useState<DataTypeStore | null>(null)
  const [triggerSearch, setTriggerSearch] = useState<boolean>(false)
  const [isViewClicked, setIsViewClicked] = useState<boolean>(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    useState<boolean>(false)
  const [listingLayout, setListingLayout] = useState<LayoutResponseType>()
  const [dupData, setDupData] = useState<FieldValues>()
  const [selectedRow, setSelectedRow] = useState<ListingTableRow | null>(null)
  const [addedRecord, setAddedRecord] = useState<FieldValues | null>(null)
  const [viewDuplicateRecord, setViewDuplicateRecord] = useState<boolean>(false)
  const [refetchKey, setRefetchKey] = useState<number>(0)
  const [showPotentialMatch, setShowPotentialMatch] = useState(false)
  const [activeTab, setActiveTab] = useState<number>(0)
  const [isPatientMinor, setIsPatientMinor] = useState(false)
  const [showPatientWarningPopup, setShowPatientWarningPopup] = useState(false)
  const [showFacilityPopup, setShowFacilityPopup] = useState(false)
  const onTabChange = (value: OnTabChangeCallbackAttributes) => {
    if (typeof value?.index !== 'number') return
    setActiveTab(value.index)
  }
  // const entityName =
  //   element?.meta?.relationship?.target_entity === 'c_organizations'
  //     ? ORGANIZATION_ENTITY.label
  //     : element?.meta?.relationship?.target_entity
  //         ?.replace('c_', '')
  //         ?.replace(/^./, (char) => char.toUpperCase())
  //         ?.replace(/s$/, '') ?? ''
  const filterFields = element?.meta?.filter
  const selectedFieldsLayout =
    fullLayout?.layout_data?.added_fields?.[
      element?.keyName || ''
    ]?.selected_fields?.map((field) => field?.name) || []
  const selectedFields = (element?.meta?.selected_fields ??
    fullLayout?.layout_data?.added_fields?.[element?.keyName || '']
      ?.selected_fields ??
    []) as SelectedField[]
  const childAddedFields = {
    // selected fields keyed by their name
    ...(keyBy(selectedFields, 'name') || {}),
    // merge with existing added_fields definitions (later keys override earlier ones)
    ...(element?.meta?.added_fields ??
      fullLayout?.layout_data?.added_fields?.[element?.keyName || '']
        ?.added_fields ??
      {}),
  } as FieldDataByFieldNameType
  const fieldKeyName = associatedObject?.isSubForm
    ? `${associatedObject?.subFormParentPath}.${element?.meta?.field_name}`
    : isAssociated
    ? associatedObject?.associatedPath + '.' + (element?.keyName || '')
    : element?.keyName
  const behaviorRuleKey = (element?.keyName ?? fieldKeyName) as string
  const associatedFieldKeyName = associatedObject?.isSubForm
    ? `${associatedObject?.subFormParentPath}.${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${element?.meta?.field_name}`
    : isAssociated
    ? `${
        associatedObject?.associatedPath ?? HYBRID_LAYOUT_CONSTANTS?.ERROR_KEY
      }.${HYBRID_LAYOUT_CONSTANTS?.NESTING_DATA_KEY}.${
        element?.keyName ?? HYBRID_LAYOUT_CONSTANTS?.ERROR_KEY
      }`
    : element?.keyName ?? HYBRID_LAYOUT_CONSTANTS?.ERROR_KEY
  const contextualObject: RootObject | null = useAppSelector(
    (state: RootState) => state.contextual?.contextualObject
  )
  const [formId, setFormId] = useState<string>(
    fullLayout ? formUniqueId ?? '' : ''
  )
  const parentDataId = useWatch({
    name: 'id',
  })
  const dataId = rest?.dataId ?? parentDataId
  // const entitiesList = useAppSelector(
  //   (state: RootState) => state.entity.entities
  // )
  const entitiesByName = useAppSelector(
    (state: RootState) => state.entity.entitiesByName
  )
  const createRelatedEntitiesFilter = (
    select: { fieldName: string; path: string; fieldKeyName: string }[],
    macroLibrary: FieldValues,
    filter: FilterObject[] | undefined
  ) => {
    return select.map((field) => {
      const value = get(macroLibrary, field.path, null)
      const result: {
        logical_operator?: string
        criteria: {
          values: unknown[]
          operation: string
          field_name: string
        }[]
      } = { criteria: [] }
      if (value) {
        result.criteria.push({
          values: [value],
          operation: 'EQUALS',
          field_name: field?.fieldName,
        })
      } else if (
        filter?.find((item) => item.fieldName === field.fieldKeyName)?.type ===
        'nullable'
      ) {
        result.criteria.push({
          values: [],
          operation: 'IS_NULL',
          field_name: field?.fieldName,
        })
      }
      if (
        filter?.find((item) => item.fieldName === field.fieldKeyName)?.type ===
        'append_null'
      ) {
        result.criteria.push({
          values: [],
          operation: 'IS_NULL',
          field_name: field?.fieldName,
        })
        result.logical_operator = 'OR'
      }
      return result.criteria.length > 0 ? result : undefined
    })
  }

  const createRelatedEntities = (
    macroLibrary: FieldValues,
    filter: FilterObject[] | undefined
  ) => {
    const relatedEntities: FieldValues = reduce(
      filterFields,
      (acc: { [key: string]: RelatedEntity }, field) => {
        const rawPath = field.macros.replace(/\$\{|\}/g, '')
        const path = rawPath.split('.')
        const key = field?.fieldName
        const fieldName = path[path.length - 1]
        const fieldKeyName = field?.fieldName

        if (key) {
          if (!acc[key]) {
            acc[key] = {
              select: [],
              filters: [],
            }
          }

          const alreadyExists = acc[key].select.some(
            (item) => item.fieldName === fieldName && item.path === rawPath
          )

          if (
            subFormMetaObject?.isSubFormMulti &&
            fullLayout?.entity_name === path?.[0]
          ) {
            path.splice(2, 0, subFormMetaObject?.subFormIndex?.toString())
          }

          const newPath = path.join('.')

          if (!alreadyExists) {
            acc[key].select.push({
              fieldName,
              fieldKeyName,
              path: newPath,
            })
          }
        }

        return acc
      },
      {}
    )

    Object.keys(relatedEntities).forEach((key) => {
      relatedEntities[key].filters = createRelatedEntitiesFilter(
        relatedEntities[key].select,
        macroLibrary,
        filter
      )
    })
    return relatedEntities
  }

  const createSelectedFields = (reduxObj: FieldValues) => {
    let selectedFields: string[] = []
    if (filterFields && selectedFields)
      if (fullLayout?.entity_name !== 'case') {
        // Need to change get from macros
        selectedFields = [
          ...(selectedFieldsLayout || []),
          ...Object.keys(reduxObj.case ?? {}), // change to macros
          ...filterFields.map((field) => {
            return field.fieldName || ''
          }), // change to macros
        ]
      } else {
        selectedFields = [
          ...(selectedFieldsLayout || []),
          ...filterFields.map((field) => {
            return field.fieldName || ''
          }), // change to macros
        ]
      }
    return selectedFields
  }
  const simplifyRelatedEntitySelects = (filterObject: FieldValues) => {
    const relatedEntities = filterObject.related_entities

    Object.keys(relatedEntities).forEach((key) => {
      const entity = relatedEntities[key]

      if (Array.isArray(entity.select)) {
        entity.select = entity.select.map(
          (item: { fieldName: string }) => item.fieldName
        )
      }
    })

    return filterObject
  }

  const createFilterObject = (
    macroLibrary: FieldValues,
    filter: FilterObject[] | undefined
  ) => {
    const filterObject = {
      entity_name: (
        fullLayout?.layout_data?.added_fields?.[element?.keyName ?? '']
          ?.relationship?.target_entity ?? ''
      ).replace(/^c_/, ''),
      select: createSelectedFields(macroLibrary),
      filters: [
        {
          logical_oprator: 'AND',
          criteria: [
            {
              values: ['ACTIVE'],
              operation: 'EQUALS',
              field_name: 'record_status',
            },
          ], // as of now will always be empty
        },
      ],
      related_entities: createRelatedEntities(macroLibrary, filter),
      page: 0,
      size: 10000,
    }
    const finalResult: FieldValues = simplifyRelatedEntitySelects(filterObject)
    fetchFilteredData(finalResult)
  }

  const fetchFilteredData = async (finalResult: FieldValues) => {
    try {
      const relatedEntities = (finalResult?.related_entities ?? {}) as Record<
        string,
        { filters?: unknown }
      >
      const hasCompleteRelatedFilters = Object.values(relatedEntities).every(
        (entity) =>
          (entity?.filters as unknown as FilterObject[])?.[0] !== undefined
      )
      const fingerprint = JSON.stringify(
        Object.entries(getValues?.() ?? {}).map(([key, entity]) => [
          key,
          entity?.filters,
        ])
      )
      const lastFingerprint = lastFilterFingerprintRef.current
      const hadComplete = hadCompleteRelatedFiltersRef.current
      const isUserParentChange =
        hadComplete &&
        hasCompleteRelatedFilters &&
        lastFingerprint !== null &&
        lastFingerprint !== fingerprint

      if (isUserParentChange) {
        hasUserChangedParentRef.current = true
      }

      const shouldAppendSelected =
        (mode === MODE_CONSTANTS.EDIT || mode === MODE_CONSTANTS.VIEW) &&
        !isUserParentChange &&
        !hasUserChangedParentRef.current

      lastFilterFingerprintRef.current = fingerprint
      hadCompleteRelatedFiltersRef.current = hasCompleteRelatedFilters

      if (!hasCompleteRelatedFilters) {
        if (shouldAppendSelected) {
          const rows = await appendSelectedOptionIfMissing([])
          setFilteredData(rows)
          setEntityData(rows)
        } else {
          setFilteredData([])
          setEntityData([])
        }
        return
      }

      const response = await getGenericEntityListingAPI(
        finalResult as GetGenericEntityListingApiType
      )
      let filteredRows = filterByAllowedCriteria(
        (response?.content ?? []) as DataTypeStore[]
      )
      if (shouldAppendSelected) {
        filteredRows = await appendSelectedOptionIfMissing(filteredRows)
      }
      setFilteredData(filteredRows)
      setEntityData(filteredRows)
    } catch (err) {
      console.error(err)
    }
  }

  const createMacrosLibrary = (
    macrosData: MacroResult[],
    fieldName: string
  ) => {
    const result: FieldValues = {}
    const entityKey = fullLayout?.entity_name
    const listArray = filterFields?.map((val) => {
      const path = val?.macros?.replace(/\$\{|\}/g, '')
      const keys = path.split('.')

      return keys[0]
    })
    const newData = macrosData?.map((val, index) => {
      if (listArray) return { [listArray[index]]: val }
    })
    newData?.forEach((item) => {
      merge(result, item)
    })
    const updatedContextualObject: FieldValues = {
      [formId]: { ...(macroRef?.current ?? {}) },
    }
    if (entityKey && formId) {
      updatedContextualObject[formId] = {
        ...(updatedContextualObject?.[formId] || {}),
        ...(result?.[entityKey]
          ? {
              [entityKey]: {
                ...(updatedContextualObject?.[formId]?.[entityKey] || {}),
                ...(subFormMetaObject?.isSubFormMulti
                  ? {
                      [fieldName]: {
                        ...(updatedContextualObject?.[formId]?.[entityKey]?.[
                          fieldName
                        ] || {}),
                        ...(!isEmpty(result?.[entityKey]?.[fieldName]) && {
                          [subFormMetaObject?.subFormIndex]:
                            result?.[entityKey]?.[fieldName],
                        }),
                      },
                    }
                  : {
                      [fieldName]: {
                        ...((
                          updatedContextualObject[formId]?.[
                            entityKey
                          ] as NestedObject
                        )?.[fieldName] || {}),
                        ...result?.[entityKey]?.[fieldName],
                      },
                    }),
              },
            }
          : {
              case: {
                ...updatedContextualObject?.[formId]?.['case'],
                ...(result?.['case']?.[fieldName] && {
                  [fieldName]: {
                    ...((
                      updatedContextualObject[formId]?.['case'] as NestedObject
                    )?.[fieldName] || {}),
                    ...result?.['case']?.[fieldName],
                  },
                }),
              },
            }),
      }
      macroRef.current = updatedContextualObject[formId]

      dispatch(
        setContextualObject({
          key: formId,
          value: macroRef?.current ?? {},
        })
      )
    }
  }

  const getCleanedMacros = (fields: FilterObject[]) => {
    return fields.map((field) => field.macros)
  }

  const resolveMacroRecordId = (
    macroPath: string[],
    caseIdQuery: string | null
  ): number | undefined => {
    if (macroPath[0] === 'case') {
      if (caseId ?? caseIdQuery) {
        return Number(caseId ?? caseIdQuery)
      }
      const isCaseEntityRoute =
        pathname.includes(`/${entitiesRoute}/`) &&
        (pathname.includes(`/${viewRoute}/`) ||
          pathname.includes(`/${editRoute}/`))
      if (isCaseEntityRoute && routeRecordId) {
        return Number(routeRecordId)
      }
    }

    if (dataId) {
      return Number(dataId)
    }

    return undefined
  }

  const fetchMacrosData = async () => {
    if (!filterFields) {
      return
    }
    const macrosArray = getCleanedMacros(filterFields)
    const searchParams = new URLSearchParams(window.location.search)
    const caseIdQuery = searchParams.get('caseId')

    try {
      await Promise.all(
        macrosArray.map(async (macro) => {
          const cleanMacros = macro.replace(/\$\{|\}/g, '')
          const path = cleanMacros.split('.')
          const recordId = resolveMacroRecordId(path, caseIdQuery)

          if (!recordId) {
            return
          }

          const { data: macroResponse } = await fetchMacrosApi(
            'macros',
            recordId,
            macrosArray
          )

          if (macroResponse) {
            setMacrosData(macroResponse)
            createMacrosLibrary(macroResponse, path?.[1])
          }
        })
      )
    } catch (err) {
      console.error(err)
    }
  }

  const toggleCreateNew = (isCreate: boolean | null) => {
    setIsCreateNew((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      isCreate: isCreate,
    }))

    if (isCreate) {
      if (isEmpty(createLayout)) {
        fetchCreateLayout()
      }
    } else {
      if (isEmpty(editLayout)) {
        fetchEditLayout()
      }
    }
  }

  const fetchCreateLayout = async () => {
    try {
      dispatch(showLoader())
      const res = await getLayoutByIdApi({
        entityId:
          element?.meta?.relationship?.target_entity_id.toString() ?? '',
        layoutId: element.meta?.create_layout_id?.toString() ?? '',
      })
      if (res?.data) {
        setCreateLayout(res.data)
      }
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchEditLayout = async () => {
    dispatch(showLoader())
    try {
      if (element?.meta?.edit_layout_id || element?.meta?.create_layout_id) {
        const res = await getLayoutByIdApi({
          entityId:
            element?.meta?.relationship?.target_entity_id.toString() ?? '',
          layoutId:
            element.meta?.edit_layout_id?.toString() ??
            element?.meta?.create_layout_id?.toString() ??
            '',
        })
        if (res?.data) {
          setEditLayout(res.data)
        }
        fetchEditRecordData(res?.data?.id ?? 0)
      } else {
        const res = await getLayoutByEntityIdAndTypeApi({
          entityId:
            element?.meta?.relationship?.target_entity_id.toString() ?? '',
          type: LAYOUT_TYPE_NAMESPACE?.VIEW_EDIT,
          isDefault: true,
        })
        if (res?.data) {
          setEditLayout(res.data?.[0])
        }
        fetchEditRecordData(res?.data?.[0]?.id ?? 0)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchEditRecordData = async (
    layoutId: number,
    recordId?: string | number
  ) => {
    try {
      dispatch(showLoader())
      const res = await getEntityData(
        element?.meta?.relationship?.target_entity ?? '',
        Number(element?.meta?.relationship?.target_entity_id),
        Number(layoutId),
        recordId
          ? recordId
          : (
              viewEntityRecord?.record_id ??
              viewEntityRecord?.id ??
              0
            ).toString() ?? ''
      )
      if (res?.data) {
        setEditRecordData(res.data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  // const fetchEntityData = async (
  //   entityId: string,
  //   entityName: string,
  //   layoutId: string
  // ) => {
  //   try {
  //     const res = await getEntityDataByEntityIdLayoutIdApi({
  //       id: entityId,
  //       name: entityName,
  //       layoutId: layoutId,
  //     })
  //     setEntityData(res.data)
  //   } catch (err) {
  //     console.log(err)
  //   }
  // } // To remove

  // const fillDropDownOptions = async (
  //   entityId: number,
  //   entityName: string,
  //   fields: SelectedField[]
  // ) => {
  //   try {
  //     dispatch(showLoader())
  //     if (!hasPermission(MODE_CONSTANTS.VIEW, entityId)) {
  //       setEntityData([])
  //       return
  //     }
  //     const res = await getEntityDataEmbedded(
  //       entityName,
  //       entityId,
  //       fields.map(({ name }) => name)
  //     )
  //     const data: DataTypeStore[] = res?.data?.content ?? []
  //     for (const field of fields) {
  //       if (!hasPermission(MODE_CONSTANTS.VIEW, entityId, field.id))
  //         data.forEach((row) => (row[field.name] = NAMESPACE.REDACTED))
  //     }
  //     setEntityData(data)
  //   } catch (err) {
  //     console.error(err)
  //   } finally {
  //     dispatch(hideLoader())
  //   }
  // }

  const generateLabelFromElementAndDataFromAPI = (
    element: ElementType,
    data?: DataTypeStore
  ) => {
    return generateLabelFromSelectedFields(
      element?.meta?.selected_fields ?? selectedFields ?? [],
      data ?? {},
      element?.meta?.relationship?.target_entity_id ?? ''
    )
  }

  const lookupVal = useWatch({
    control,
    name: fieldKeyName as Path<T>,
    // defaultValue: "default", // default value before the render
  })

  const filterByAllowedCriteria = useCallback(
    (rows: DataTypeStore[]) => {
      const filter = behaviorLookupFilter
      if (!filter) return rows
      const allowedIds =
        filter.allowedIds !== undefined ? new Set(filter.allowedIds) : null
      const allowedLabels =
        filter.allowedLabels !== undefined
          ? new Set(filter.allowedLabels)
          : null
      const selectedId = Number(lookupVal?.id)
      return rows.filter((row) => {
        const rowId = Number(row.id)
        if (!Number.isNaN(selectedId) && rowId === selectedId) return true
        if (allowedIds && !allowedIds.has(rowId)) return false
        if (allowedLabels) {
          const label = generateLabelFromElementAndDataFromAPI(
            element,
            row
          ).trim()
          if (!allowedLabels.has(label)) return false
        }
        return true
      })
    },
    [behaviorLookupFilter, lookupVal?.id, element]
  )

  const handleSubmit = async (values: FieldValues) => {
    try {
      dispatch(showLoader())
      const res = await addEntityData(
        element?.meta?.relationship?.target_entity ?? '',
        Number(element?.meta?.relationship?.target_entity_id),
        isCreateNew?.isCreate
          ? Number(createLayout?.id)
          : Number(editLayout?.id),
        values
      )
      if (res.status === 200) {
        addToaster({
          id: uniqueId(),
          message: TOASTER_NAMESPACE?.ADD_SUCCESS,
          type: 'success',
        })
        setValue(
          fieldKeyName as Path<T>,
          { id: Number(res.data.id) } as PathValue<T, Path<T>>
        )
        // setValue(
        //   `${element.keyName}.id` as Path<T>,
        //   Number(res.data.id) as PathValue<T, Path<T>>
        // )
        setRefetchOptions(!refetchOptions)
        setRefetchSelectedId(!refetchSelectedId)
        setIsCreateNew((prev) => ({
          ...prev,
          isOpen: false,
          isCreate: null,
        }))
      } else {
        addToaster({
          id: uniqueId(),
          message: TOASTER_NAMESPACE?.ENTER_VALID_DETAILS,
          type: 'error',
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const changeContextValue = (val: IDObject, clearKeys: string[]) => {
    const formKey = formId
    const entityKey = fullLayout?.entity_name
    const fieldKeyName = element?.meta?.field_name

    if (!formKey || !entityKey || !fieldKeyName) return
    const keys = [fieldKeyName, ...(clearKeys || [])]

    const updatedContextualObject: FieldValues = { ...contextualObject }
    keys.forEach((fieldKey) => {
      updatedContextualObject[formKey] = {
        ...(updatedContextualObject[formKey] || {}),
        [entityKey]: {
          ...(updatedContextualObject[formKey]?.[entityKey] || {}),
          ...(subFormMetaObject?.isSubFormMulti
            ? {
                [fieldKey]: {
                  ...updatedContextualObject?.[formKey]?.[entityKey]?.[
                    fieldKey
                  ],
                  [subFormMetaObject?.subFormIndex]: {
                    ...(updatedContextualObject?.[formKey]?.[entityKey]?.[
                      fieldKey
                    ]?.[subFormMetaObject?.subFormIndex] ?? {}),
                    id: fieldKey === fieldKeyName ? val?.id : '',
                  },
                },
              }
            : {
                [fieldKey]: {
                  ...((
                    updatedContextualObject[formKey]?.[
                      entityKey
                    ] as NestedObject
                  )?.[fieldKey] || {}),
                  id: fieldKey === fieldKeyName ? val?.id : '',
                },
              }),
        },
      }
    })
    if (entityKey && fieldKeyName) {
      dispatch(
        setContextualObject({
          key: formKey,
          value: updatedContextualObject[formKey],
        })
      )
    }
  }

  const renderDropdownIcon = (
    disabled: boolean,
    closeOptions: () => void
  ): JSX.Element => {
    // TODO: Need to improve if logics
    return (
      <div className="flex items-center" data-qa-id={dataQaId ?? ''}>
        {lookupVal?.id &&
        (element?.meta?.edit_layout_id || element?.meta?.create_layout_id) ? (
          <button
            type="button"
            className="cursor-pointer pr-[6px]"
            onClick={(e) => {
              // todo - can be changed in future
              // if (!_disabled) {
              closeOptions()
              e.preventDefault()
              e.stopPropagation()
              toggleCreateNew(false)
              // }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                closeOptions()
                e.stopPropagation()
                e.preventDefault()
                toggleCreateNew(false)
              }
            }}
            tabIndex={rest?.tabIndex}
          >
            <SvgEditBlue />
          </button>
        ) : (
          lookupVal?.id && <SvgDropdownArrowGrey fill="#0A6FCD" />
        )}
        {element?.meta?.relationship?.target_entity &&
        !disabled &&
        !lookupVal?.id &&
        CONFIG_STRINGS?.ADVANCE_SEARCH_ENTITIES?.includes(
          element.meta.relationship.target_entity
        ) ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              closeOptions()
              e.stopPropagation()
              setIsAdvancedSearch(!isAdvancedSearch)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                closeOptions()
                e.stopPropagation()
                setIsAdvancedSearch(!isAdvancedSearch)
              }
            }}
            tabIndex={rest?.tabIndex}
            disabled={disabled}
          >
            <SvgSearchIcon color="#0A6FCD" />
          </button>
        ) : !isSingleLookupEntityGlobal() &&
          !lookupVal?.id &&
          element?.meta?.create_layout_id ? (
          <>
            <SvgPipe height="12" strokeWidth="3" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                closeOptions()
                e.stopPropagation()
                toggleCreateNew(true)
                // }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  closeOptions()
                  e.stopPropagation()
                  e.preventDefault()
                  toggleCreateNew(true)
                }
              }}
              tabIndex={rest?.tabIndex}
            >
              <SvgAdd color="#0A6FCD" />
            </button>
          </>
        ) : (
          !lookupVal?.id && <SvgDropdownArrowGrey fill="#0A6FCD" />
        )}
      </div>
    )
  }

  const previousRefetchOptions = usePrevious(refetchOptions)

  const contextualObjectString = useMemo(
    () => JSON.stringify(contextualObject ?? {}),
    [contextualObject, mode]
  )

  const fetchOptionsById = async (id: string) => {
    const getSelectedOption = await getGenericEntityListingAPI({
      entity_id: element?.meta?.relationship?.target_entity_id.toString() ?? '',
      select: (element?.meta?.selected_fields ?? selectedFields)?.map(
        (field) => field.name
      ) as string[],
      filters: [
        {
          criteria: [
            {
              values: [id],
              operation: 'EQUALS',
              field_name: 'id',
            },
          ],
        },
      ],
    })
    return getSelectedOption
  }

  const appendSelectedOptionIfMissing = async (
    rows: DataTypeStore[]
  ): Promise<DataTypeStore[]> => {
    const selectedId = getValues?.(fieldKeyName as Path<T>)?.id ?? lookupVal?.id
    if (!selectedId) return rows
    if (rows.some((row) => Number(row.id) === Number(selectedId))) return rows
    const selected = await fetchOptionsById(String(selectedId))
    if (!isEmpty(selected?.[0])) {
      return [...rows, selected[0] as DataTypeStore]
    }
    return rows
  }

  const fetchSearchedOptions = async (searchValue: string) => {
    const lookUpValLatest = getValues?.(fieldKeyName as Path<T>) ?? lookupVal
    try {
      const criteriaArray =
        (element?.meta?.selected_fields ?? selectedFields) && searchValue.trim()
          ? (element?.meta?.selected_fields ?? selectedFields)?.map((field) => {
              return {
                field_name: field.name,
                operation: 'LIKE',
                preceding_condition: 'OR',
                values: searchValue.split('|').map((term) => term.trim()),
              }
            })
          : defaultValue
          ? [
              {
                values: [defaultValue],
                operation: 'NOT_EQUALS',
                field_name: 'id',
              },
            ]
          : lookUpValLatest
          ? [
              {
                values: [lookUpValLatest.id],
                operation: 'NOT_EQUALS',
                field_name: 'id',
              },
            ]
          : []
      document.body.classList.add('cursor-wait')
      const res = await getGenericEntityListingAPI({
        entity_id:
          element?.meta?.relationship?.target_entity_id.toString() ?? '',
        select: (element?.meta?.selected_fields ?? selectedFields)?.map(
          (field) => field.name
        ) as string[],
        filters: [
          {
            criteria: criteriaArray,
          },
        ],
      })

      if (defaultValue && isEmpty(lookUpValLatest) && mode === 'add') {
        const getDefaultOption = await fetchOptionsById(defaultValue)
        if (!isEmpty(getDefaultOption?.[0])) {
          res.push(getDefaultOption?.[0])
        }
      } else {
        const getSelectedOption = await fetchOptionsById(lookUpValLatest?.id)
        if (!isEmpty(getSelectedOption?.[0])) {
          res.push(getSelectedOption?.[0])
        }
      }

      if (res) {
        setEntityData(filterByAllowedCriteria(res as DataTypeStore[]))
      }
    } catch (err) {
      console.error(err)
    } finally {
      document.body.classList.remove('cursor-wait')
    }
  }

  const triggerClick = () => {
    if (buttonRef.current) {
      buttonRef.current.click()
    }
  }

  const editSelectedRecordAdvanceSearh = async (values: FieldValues) => {
    try {
      dispatch(showLoader())
      if (editEntityRecord) {
        const res = await editEntityData(
          element?.meta?.relationship?.target_entity ?? '',
          Number(element?.meta?.relationship?.target_entity_id),
          Number(element?.meta?.create_layout_id ?? editLayout?.id ?? 0),
          values,
          (
            viewEntityRecord?.record_id ??
            viewEntityRecord?.id ??
            0
          ).toString() ?? ''
        )
        if (res?.data) {
          dispatch(
            addToaster({
              id: uniqueId(),
              message: TOASTER_NAMESPACE?.ADD_SUCCESS,
              type: 'success',
            })
          )
          setRefetchSelectedId(!refetchSelectedId)
          setRefetchOptions(!refetchOptions)
          cancelClick()
          setTriggerSearch(!triggerSearch)
        }
      }
      if (addEntityRecord) {
        const entity = entitiesByName[editLayout?.entity_name ?? '']

        const flagValue = validatePhoneNumberAndAddress(values, dispatch)
        if (flagValue) {
          return
        }
        if (entity?.is_duplicate) {
          const listingLayout = await fetchRequestorLayout()
          fetchTableDataCallback(values, listingLayout?.id)
          setEditEntityRecord(false)
          setIsViewClicked(false)
          setAddedRecord(values)
        } else {
          const res = await addEntityData(
            element?.meta?.relationship?.target_entity ?? '',
            Number(element?.meta?.relationship?.target_entity_id),
            Number(editLayout?.id),
            values
          )
          if (res?.data) {
            addToaster({
              id: uniqueId(),
              message: TOASTER_NAMESPACE?.ADD_SUCCESS,
              type: 'success',
            })
            setValue(
              fieldKeyName as Path<T>,
              { id: Number(res.data.id) } as PathValue<T, Path<T>>
            )
            setRefetchSelectedId(!refetchSelectedId)
            setRefetchOptions(!refetchOptions)
            setIsAdvancedSearch(false)
            setIsDuplicateModalOpen(false)
            cancelClick()
          }
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const createNewRecord = async (values?: FieldValues) => {
    try {
      const res = await addEntityData(
        element?.meta?.relationship?.target_entity ?? '',
        Number(element?.meta?.relationship?.target_entity_id),
        Number(editLayout?.id),
        addedRecord ?? values ?? {}
      )
      if (res?.data) {
        addToaster({
          id: uniqueId(),
          message: TOASTER_NAMESPACE?.ADD_SUCCESS,
          type: 'success',
        })
        setValue(
          element.keyName as Path<T>,
          { id: Number(res.data.id) } as PathValue<T, Path<T>>
        )
        setAddedRecord(null)
        setRefetchSelectedId(!refetchSelectedId)
        setRefetchOptions(!refetchOptions)
        setIsAdvancedSearch(false)
        setIsDuplicateModalOpen(false)
        cancelClick()
      }
    } catch (err) {
      console.error(err)
    }
  }

  const cancelClick = () => {
    setAddEntityRecord(false)
    setEditEntityRecord(false)
    setIsViewClicked(false)
    setAddedRecord(null)
  }

  const fetchViewLayout = async () => {
    dispatch(showLoader())
    try {
      const res = await getLayoutByEntityIdAndTypeApi({
        entityId:
          element?.meta?.relationship?.target_entity_id.toString() ?? '',
        type: LAYOUT_TYPE_NAMESPACE?.OVERVIEW_TYPE,
        isDefault: true,
      })
      if (res?.data) {
        setViewLayout(res.data?.[0])
      }
      fetchEditRecordData(res?.data?.[0]?.id ?? 0)
      return res?.data?.[0]?.id
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchRequestorLayout = async () => {
    try {
      // let patientViewId = 0
      // let providerViewId = 0

      // entitiesList.forEach((entity: { name: string; id: number }) => {
      //   if (entity.name === ENTITY_NAME.PATIENT_VIEW_KEY) {
      //     patientViewId = entity.id ?? 0
      //   } else if (entity.name === ENTITY_NAME.PROVIDER_VIEW_KEY) {
      //     providerViewId = entity.id ?? 0
      //   }
      // })
      // let response
      // if (entityName === NAMESPACES.PATIENT_KEY) {
      //   response = await getAllLayoutApi({
      //     entityId: patientViewId?.toString() ?? '0',
      //     defaultValue: true,
      //     type: LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING,
      //   })
      //   setListingLayout(response?.data?.content?.[0])
      // } else if (entityName === NAMESPACES.PROVIDER_KEY) {
      //   response = await getAllLayoutApi({
      //     entityId: providerViewId?.toString() ?? '0',
      //     defaultValue: true,
      //     type: LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING,
      //   })
      //   setListingLayout(response?.data?.content?.[0])
      // } else {
      //   response = await getLayoutByName({
      //     entityId: element?.meta?.relationship?.parent_entity_id.toString(),
      //     layoutName: 'Requestor Search Layout',
      //   })
      //   setListingLayout(response?.data)
      // }
      const res = await getLayoutByEntityIdAndTypeApi({
        entityId:
          element?.meta?.relationship?.target_entity_id.toString() ?? '',
        type: LAYOUT_TYPE_NAMESPACE?.LAYOUT_LISTING,
        isDefault: true,
      })
      if (res?.data) {
        setListingLayout(res.data?.[0])
        return res.data?.[0]
      }
    } catch (err) {
      console.error(err)
    }
  }

  const updatePatientInRedux = async (
    entityName: string,
    entityId: number,
    id: number
  ) => {
    if (entityName === ENTITY_NAMES_RELATION_MAPPING?.PATIENT) {
      if (id > 0) {
        const requiredData = await getEntityDataByIdEmbedded(
          entityName,
          entityId,
          [
            PATIENT_LAYOUT_KEYWORDS?.FIRST_NAME,
            PATIENT_LAYOUT_KEYWORDS?.LAST_NAME,
            PATIENT_LAYOUT_KEYWORDS?.DATE_OF_BIRTH,
          ],
          id
        )
        dispatch(updatePatientData(requiredData?.data))
      } else {
        dispatch(updatePatientData(null))
      }
    }
  }

  const fetchTableDataCallback = async (
    values: FieldValues,
    listingLayoutId: string | number,
    payload?: getEntityDataByEntityFilter['payload'] & { layout_id: number }
  ) => {
    dispatch(showLoader())
    try {
      if (listingLayoutId) {
        const duplicateApiPayload = transformToDuplicateApiPayload(
          values,
          element?.meta?.relationship?.target_entity_id.toString() ?? '',
          listingLayoutId.toString(),
          editLayout
        )
        const res = await fetchDuplicateRecordsAPI(
          element?.meta?.relationship?.target_entity_id ?? 0,
          duplicateApiPayload,
          payload?.page as number,
          payload?.size as number
        )
        const addSelectedFlag = (data: ListingTableRow[]) => {
          return data.map((row) => ({
            ...row,
            select: row.id === selectedRow?.id ? true : false,
          }))
        }
        setDupData(res?.data)
        res.data.content = addSelectedFlag(res?.data?.content)

        if (res?.data?.content.length != 0) {
          setIsDuplicateModalOpen(true)
        } else {
          createNewRecord(values)
        }

        return res?.data
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const isSingleLookupEntityGlobal = () => {
    const elementKey = element.keyName
    const targetEntityId =
      fullLayout?.layout_data?.added_fields?.[elementKey]?.relationship
        ?.target_entity_id
    const entityData = Object.values(entitiesByName).find(
      (obj) => obj.id === targetEntityId
    )
    const isGlobal = entityData ? entityData?.source_type === 'GLOBAL' : null
    return isGlobal
  }
  const triggerToClearRelatedFields = () => {
    if (isEmpty(entityData)) {
      const keykey = associatedObject?.isSubForm
        ? `${associatedObject?.subFormParentPath}.${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${element?.meta?.field_name}`
        : isAssociated
        ? associatedFieldKeyName
        : `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${associatedFieldKeyName}`
      Object.keys(childAddedFields).forEach((key) => {
        const keyNameToChange = isCustomForm
          ? `${keykey}.${key}`
          : getKeyNameForDisabledFieldsOfSingleLookup(
              associatedFieldKeyName,
              key
            )
        setValue(keyNameToChange as Path<T>, '' as PathValue<T, Path<T>>)
      })
      changeContextValue({ id: '' }, [])
      setValue(
        element?.meta?.field_name as Path<T>,
        '' as PathValue<T, Path<T>>
      )
    }
  }

  useEffect(() => {
    let isParent = true

    const tempArray: string[][] = []
    if (Array.isArray(element?.meta?.filter)) {
      element.meta.filter.forEach((filter) => {
        const path = filter?.macros?.replace(/\$\{|\}/g, '').split('.')
        if (path?.length) tempArray.push(path)
      })
    }

    for (const path of tempArray) {
      const formObj = contextualObject?.[formId]
      const entityObj = formObj?.[path[0]]
      const fieldObj = (entityObj as FieldValues)?.[path[1]]
      const value = fieldObj?.[path[2]]

      if (value === '') {
        isParent = false
        break
      }
    }

    if (!isEmpty(element?.meta?.filter) && !isParent) {
      triggerToClearRelatedFields()
    }
  }, [entityData])
  useEffect(() => {
    if (lookupVal && Object.keys(lookupVal).length > 1) {
      setSelectedOptionData(lookupVal)
      setValue(
        fieldKeyName as Path<T>,
        { id: lookupVal?.id } as PathValue<T, Path<T>>
      )
      setRefetchSelectedId(!refetchSelectedId)
    }
    // TODO: to be deleted after testing
    // else if (defaultValue && mode === 'add') {
    //   setValue(
    //     element.keyName as Path<T>,
    //     { id: Number(defaultValue) } as PathValue<T, Path<T>>
    //   )
    // }
  }, [lookupVal])

  useEffect(() => {
    if (!lookupVal && defaultValue && mode === 'add') {
      setValue(
        fieldKeyName as Path<T>,
        { id: Number(defaultValue) } as PathValue<T, Path<T>>
      )
    }
  }, [])
  const handlePatientSelection = useCallback(
    (patient: DataTypeStore) => {
      const dob = patient?.date_of_birth
      if (!dob) return

      const isMinor = evaluateFormula('isMinor({date_of_birth})', {
        date_of_birth: dob,
      })
      setIsPatientMinor(isMinor === NAMESPACES.YES)
      setShowPatientWarningPopup(true)
    },
    [setShowPatientWarningPopup, setIsPatientMinor]
  )

  const handleFacilitySelection = useCallback(
    (facility: DataTypeStore) => {
      if (!facility) return
      setShowFacilityPopup(true)
    },
    [setShowFacilityPopup]
  )

  const entityHandlers: Record<string, (entity: DataTypeStore) => void> = {
    [ENTITY_NAMES_RELATION_MAPPING.PATIENT]: handlePatientSelection,
    [ENTITY_NAMES_RELATION_MAPPING.FACILITY]: handleFacilitySelection,
  }

  useUpdateEffect(() => {
    if (
      !entityData ||
      !lookupVal?.id ||
      !element.meta.relationship?.target_entity
    )
      return

    const selectedEntity = entityData.find((v) => v.id === lookupVal.id)
    if (!selectedEntity) return

    const handler = entityHandlers[element.meta.relationship?.target_entity]
    handler?.(selectedEntity)
  }, [entityData, lookupVal?.id ?? true])

  useEffect(() => {
    const fillOtherDisabledFields = async (
      entityId: number,
      entityName: string,
      columns: string[],
      id: string | number,
      lookupKeyName: string
    ) => {
      try {
        const res = await getEntityDataByIdEmbedded(
          entityName,
          entityId,
          columns,
          id
        )

        formatDataBeforeLoad(res.data, childAddedFields ?? {})
        // setValue(`disabled_${lookupKeyName}` as Path<T>, res.data)
        const keykey = associatedObject?.isSubForm
          ? `${associatedObject?.subFormParentPath}.${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${element?.meta?.field_name}`
          : isAssociated
          ? lookupKeyName
          : `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${lookupKeyName}`
        columns.forEach((key) => {
          const keyNameToChange = isCustomForm
            ? `${keykey}.${key}`
            : getKeyNameForDisabledFieldsOfSingleLookup(lookupKeyName, key)
          setValue(keyNameToChange as Path<T>, res.data[key])
        })
      } catch (err) {
        console.error(err)
      }
    }

    if (
      element.meta.relationship?.target_entity_id &&
      element.meta.relationship?.target_entity &&
      childAddedFields &&
      lookupVal !== undefined &&
      entityData !== null &&
      entityData.length > 0
    ) {
      if (lookupVal === '') {
        // happens when we clear this field
        const keykey = associatedObject?.isSubForm
          ? `${associatedObject?.subFormParentPath}.${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${element?.meta?.field_name}`
          : isAssociated
          ? associatedFieldKeyName
          : `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${associatedFieldKeyName}`
        Object.keys(childAddedFields).forEach((val) => {
          const fieldKeyNameFinal = isCustomForm
            ? `${keykey}.${val}`
            : getKeyNameForDisabledFieldsOfSingleLookup(element.keyName, val)
          setValue(fieldKeyNameFinal as Path<T>, '' as PathValue<T, Path<T>>)
        }) // TODO: not all values are cleared correctly by setting empty string.
      } else {
        fillOtherDisabledFields(
          element.meta.relationship?.target_entity_id,
          element.meta.relationship?.target_entity,
          Object.keys(childAddedFields),
          lookupVal?.id,
          associatedFieldKeyName
        )
      }
    }
    if (!entityData) return
    const data = entityData?.find((v) => v?.id === lookupVal?.id)
    const labelVal = generateLabelFromElementAndDataFromAPI(element, data)
    setValue(
      `lookupLabels.${element.keyName}` as Path<T>,
      labelVal as PathValue<T, Path<T>>
    )
  }, [lookupVal?.id, entityData, refetchSelectedId])

  useEffect(() => {
    // if (!element.meta.selected_fields) return
    // if (!element.meta.target_entity_id) return

    // if (!hasPermission(MODE_CONSTANTS.VIEW, element.meta.target_entity_id)) {
    //   setEntityData([])
    //   return
    // }

    // const data: DataTypeStore[] = filteredData ?? []
    // for (const field of element.meta.selected_fields) {
    //   if (
    //     !hasPermission(
    //       MODE_CONSTANTS.VIEW,
    //       element.meta.target_entity_id,
    //       field.id
    //     )
    //   )
    //     data.forEach((row) => (row[field.name] = NAMESPACE.REDACTED))
    // }
    if (
      !isEmpty(filteredData) &&
      lookupVal?.id &&
      !filteredData?.some((item) => item.id === lookupVal.id)
    ) {
      setValue(fieldKeyName as Path<T>, '' as PathValue<T, Path<T>>)
    }
  }, [filteredData])

  useEffect(() => {
    if (behaviorLookupFilter !== undefined) {
      setRefetchOptions((prev) => !prev)
    }
  }, [behaviorLookupFilter])

  useEffect(() => {
    if (contextualObject?.[formId || '']) {
      macroRef.current = contextualObject?.[formId ?? '']
      if (element?.meta?.filter) {
        createFilterObject(
          contextualObject?.[formId ?? ''],
          element?.meta?.filter
        )
      }
    }
  }, [contextualObjectString, refetchOptions, formId, element?.meta?.filter])

  useEffect(() => {
    if (element?.meta?.filter && mode !== MODE_CONSTANTS?.VIEW && formId) {
      if (!macrosData?.length) {
        fetchMacrosData()
      }
    } else if (
      element.meta.relationship?.target_entity_id &&
      element.meta.relationship?.target_entity &&
      (!element.meta?.filter || mode === MODE_CONSTANTS?.VIEW) &&
      (element.meta.selected_fields ?? selectedFields) &&
      (entityData === null || previousRefetchOptions !== refetchOptions)
      // either the first time or when refetchOptions changes
    ) {
      setEntityData([])
      // fillDropDownOptions(
      //   element.meta.relationship?.target_entity_id,
      //   element.meta.relationship?.target_entity,
      //   element.meta.selected_fields ?? []
      // )
      fetchSearchedOptions('')
    }
  }, [refetchOptions, dataId, formId, mode])

  useEffect(() => {
    if (fullLayout) {
      setFormId(formUniqueId)
    }
  }, [formUniqueId])

  useEffect(() => {
    const updateRedux = async () => {
      await updatePatientInRedux(
        element?.meta?.relationship?.target_entity ?? '',
        element?.meta?.relationship?.target_entity_id ?? -1,
        lookupVal?.id ?? -1
      )
    }

    updateRedux()
  }, [lookupVal?.id])

  useEffect(() => {
    if (
      selectedOptionData &&
      entityData &&
      entityData?.length > 0 &&
      !entityData?.some((data) => data?.id === selectedOptionData?.id) &&
      (!element?.meta?.filter || mode === MODE_CONSTANTS?.VIEW)
    ) {
      setEntityData((prevData) => {
        const updatedData = [...(prevData || []), selectedOptionData]
        return updatedData
      })
    }
  }, [entityData, selectedOptionData])

  useEffect(() => {
    const searchedOptions =
      entityData
        ?.filter((data) => {
          return (
            data['record_status'] === RECORD_STATUS.ACTIVE ||
            data?.id === lookupVal?.id
          )
        })
        ?.map((data: DataTypeStore) => {
          return {
            label: generateLabelFromElementAndDataFromAPI(element, data),
            value: { id: data.id } as unknown as string,
          }
          // as DropdownType<T,{ name: string; id: number }>
        })
        ?.filter((option) => {
          if (!element?.meta?.filter) {
            return true
          }
          if (searchValue.includes('|')) {
            const searchTerms = searchValue
              .split('|')
              .map((term) => term.trim())
            return searchTerms.some((term) =>
              option.label.toLowerCase().includes(term.toLowerCase())
            )
          }
          return option.label.toLowerCase().includes(searchValue.toLowerCase())
        })
        ?.sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        ) ?? []

    setOptions(searchedOptions)
  }, [element, entityData, lookupVal?.id, searchValue])

  useEffect(() => {
    if (
      (viewEntityRecord?.record_id ?? viewEntityRecord?.id) &&
      !editEntityRecord &&
      !addEntityRecord &&
      !isViewClicked
    ) {
      setValue(
        fieldKeyName as Path<T>,
        {
          id: Number(viewEntityRecord?.record_id ?? viewEntityRecord?.id),
        } as PathValue<T, Path<T>>
      )
      setSelectedOptionData(viewEntityRecord)
      // setRefetchSelectedId((prev) => !prev)
      if (isEmpty(element?.meta?.filter)) {
        fetchSearchedOptions('')
      }
      setEntityData([])
      setIsAdvancedSearch(false)
      fetchEditLayout()
    }
  }, [viewEntityRecord])

  useEffect(() => {
    if (isAdvancedSearch === false) setShowPotentialMatch(false)
  }, [isAdvancedSearch])

  useEffect(() => {
    if (editEntityRecord || addEntityRecord) {
      fetchEditLayout()
    }
    if (viewEntityRecord) {
      fetchViewLayout()
    }
  }, [editEntityRecord, addEntityRecord, isViewClicked])

  useEffect(() => {
    const handleShowPotentialMatch = (event: Event) => {
      const customEvent = event as CustomEvent<{ fieldKeyName: string }>
      if (customEvent.detail.fieldKeyName === fieldKeyName) {
        setShowPotentialMatch(true)
        setIsAdvancedSearch(true)
      }
    }

    const hasMatchResult =
      fieldKeyName && fieldKeyEntityMapping[fieldKeyName as DedupeFieldNameType]

    if (hasMatchResult) {
      window.addEventListener(
        'showPotentialMatchTab',
        handleShowPotentialMatch as EventListener
      )
    }

    return () => {
      window.removeEventListener(
        'showPotentialMatchTab',
        handleShowPotentialMatch as EventListener
      )
    }
  }, [fieldKeyName])

  const fetchAndSetLookupValue = async (lpId: number | string) => {
    setValue(
      fieldKeyName as Path<T>,
      { id: Number(lpId) } as PathValue<T, Path<T>>
    )
    fetchSearchedOptions('')
  }

  const CancelActionButton = (
    <CustomButton
      onClick={() => {
        setIsAdvancedSearch(false)
        setEditEntityRecord(false)
        setAddEntityRecord(false)
      }}
    >
      {APPLICATION_PREFERENCES_STRINGS?.CANCEL}
    </CustomButton>
  )

  const tabs = (fieldKeyName: keyof typeof fieldKeyStagingMapping) => [
    {
      key: 'potential_match',
      label: APPLICATION_PREFERENCES_STRINGS.POTENTIAL_MATCH_LIST,
      content: (
        <PotentialMatchTab
          apiData={
            automationStagedDetails?.strategyOutput?.result[
              fieldKeyStagingMapping?.[fieldKeyName]
            ]
          }
          entityName={fieldKeyEntityMapping[fieldKeyName]}
          closePotentialMatchTab={() => {
            setIsAdvancedSearch(false)
          }}
          fetchAndSetLookupValue={fetchAndSetLookupValue}
        />
      ),
    },
    {
      key: 'advanced_search',
      label: APPLICATION_PREFERENCES_STRINGS.ADVANCED_SEARCH,
      content: (
        <AdvancedSearchTab
          element={element}
          editEntityRecord={editEntityRecord}
          addEntityRecord={addEntityRecord}
          isViewClicked={isViewClicked}
          isDuplicateModalOpen={isDuplicateModalOpen}
          viewDuplicateRecord={viewDuplicateRecord}
          editLayout={editLayout}
          viewLayout={viewLayout}
          listingLayout={listingLayout}
          editRecordData={editRecordData}
          addedRecord={addedRecord}
          viewEntityRecord={viewEntityRecord}
          advancedFiltersData={advancedFiltersData}
          dupData={dupData}
          selectedRow={selectedRow}
          refetchKey={refetchKey}
          buttonRef={buttonRef}
          triggerSearch={triggerSearch}
          fieldKeyName={fieldKeyName}
          showHeader={false}
          cancelClick={cancelClick}
          triggerClick={triggerClick}
          editSelectedRecordAdvanceSearh={editSelectedRecordAdvanceSearh}
          createNewRecord={createNewRecord}
          setAdvancedFiltersData={setAdvancedFiltersData}
          setViewEntityRecord={setViewEntityRecord}
          setEditEntityRecord={setEditEntityRecord}
          setAddEntityRecord={setAddEntityRecord}
          setIsViewClicked={setIsViewClicked}
          setIsDuplicateModalOpen={setIsDuplicateModalOpen}
          setSelectedRow={setSelectedRow}
          setValue={setValue}
          setIsAdvancedSearch={setIsAdvancedSearch}
          setRefetchKey={setRefetchKey}
          setViewDuplicateRecord={setViewDuplicateRecord}
          fetchTableDataCallback={fetchTableDataCallback}
          fetchViewLayout={fetchViewLayout}
          fetchEditRecordData={fetchEditRecordData}
        />
      ),
    },
  ]
  const { result: matchResult, records } = (fieldKeyEntityMapping[
    fieldKeyName as DedupeFieldNameType
  ] &&
    Object.entries(automationStagedDetails?.dedup_results || {})?.find(
      ([key]) => {
        return (
          key ===
          fieldKeyEntityMapping[
            fieldKeyName as DedupeFieldNameType
          ].toLowerCase()
        )
      }
    )?.[1]) || { result: '', records: 0 }

  const dedupeResult =
    ENTITY_MAP[fieldKeyName as keyof typeof ENTITY_MAP] && matchResult
      ? getDedupeResult(fieldKeyName, matchResult, records)
      : null

  const highlightClass = dedupeResult
    ? `rounded-md border-2 ${DEDUPE_BORDER_MAP[dedupeResult]}`
    : ''

  const isEditMode = Boolean(editEntityRecord)
  const isAddMode = Boolean(addEntityRecord)

  return (
    <div className="relative">
      {isCreateNew?.isOpen && (
        <AddDataModal formUniqueId={formUniqueId}>
          <div>
            <Modal
              isOpen={isCreateNew?.isOpen}
              closeModal={() => {
                setIsCreateNew((prev) => ({
                  ...prev,
                  isOpen: false,
                  isCreate: null,
                }))
              }}
              width="min-w-[50vw] max-w-[75vw]"
              showCloseButton={false}
              isResizable
            >
              <div>
                {editLayout || createLayout ? (
                  <ShowEditLayout
                    editLayout={
                      isCreateNew?.isCreate ? createLayout : editLayout
                    }
                    element={element}
                    handleSubmit={handleSubmit}
                    lookupVal={lookupVal}
                    refetchOptions={refetchOptions}
                    refetchSelectedId={refetchSelectedId}
                    setIsCreateNew={setIsCreateNew}
                    setRefetchOptions={setRefetchOptions}
                    setRefetchSelectedId={setRefetchSelectedId}
                    toggleCreateNew={toggleCreateNew}
                    isCreateNew={isCreateNew}
                  />
                ) : (
                  <ShowNoLayoutSection
                    isLoading={isLoading}
                    setIsCreateNew={setIsCreateNew}
                    isCreateNew={isCreateNew}
                  />
                )}
              </div>
            </Modal>
          </div>
        </AddDataModal>
      )}
      {isAdvancedSearch && (
        <AddDataModal formUniqueId={formUniqueId}>
          <div>
            <Modal
              isOpen={isAdvancedSearch || showPotentialMatch}
              closeModal={() => {
                setIsAdvancedSearch(false)
              }}
              showCloseButton={false}
              width="min-w-[50vw] max-w-[50vw] max-h-[55vh]"
              isDragable
              isBackdropInteractive
              isResizable
            >
              {showPotentialMatch &&
              Object.keys(fieldKeyStagingMapping).includes(fieldKeyName) ? (
                <TabSplits
                  tabs={tabs(fieldKeyName as DedupeFieldNameType)}
                  activeTabIndex={activeTab}
                  onTabChange={onTabChange}
                  isShadow={false}
                  commonActionComponent={
                    <div className="flex gap-2">
                      {isEditMode || isAddMode || isViewClicked ? (
                        <>
                          <CustomButton onClick={cancelClick}>
                            {APPLICATION_PREFERENCES_STRINGS?.CANCEL}
                          </CustomButton>
                          {isViewClicked ? (
                            <CustomButton
                              isFilled
                              onClick={() => {
                                setValue(
                                  (fieldKeyName ?? element.keyName) as Path<T>,
                                  {
                                    id: Number(
                                      viewEntityRecord?.record_id ??
                                        viewEntityRecord?.id
                                    ),
                                  } as PathValue<T, Path<T>>
                                )
                                cancelClick()
                                setIsAdvancedSearch(false)
                              }}
                            >
                              {APPLICATION_PREFERENCES_STRINGS?.SELECT}
                            </CustomButton>
                          ) : (
                            <CustomButton isFilled onClick={triggerClick}>
                              {isEditMode &&
                                APPLICATION_PREFERENCES_STRINGS?.UPDATE}
                              {isAddMode &&
                                APPLICATION_PREFERENCES_STRINGS?.NEXT}
                            </CustomButton>
                          )}
                        </>
                      ) : (
                        CancelActionButton
                      )}
                    </div>
                  }
                />
              ) : (
                <AdvancedSearchTab
                  element={element}
                  editEntityRecord={editEntityRecord}
                  addEntityRecord={addEntityRecord}
                  isViewClicked={isViewClicked}
                  isDuplicateModalOpen={isDuplicateModalOpen}
                  viewDuplicateRecord={viewDuplicateRecord}
                  editLayout={editLayout}
                  viewLayout={viewLayout}
                  listingLayout={listingLayout}
                  editRecordData={editRecordData}
                  addedRecord={addedRecord}
                  viewEntityRecord={viewEntityRecord}
                  advancedFiltersData={advancedFiltersData}
                  dupData={dupData}
                  selectedRow={selectedRow}
                  refetchKey={refetchKey}
                  buttonRef={buttonRef}
                  triggerSearch={triggerSearch}
                  fieldKeyName={fieldKeyName}
                  showHeader={true}
                  cancelClick={cancelClick}
                  triggerClick={triggerClick}
                  editSelectedRecordAdvanceSearh={
                    editSelectedRecordAdvanceSearh
                  }
                  createNewRecord={createNewRecord}
                  setAdvancedFiltersData={setAdvancedFiltersData}
                  setViewEntityRecord={setViewEntityRecord}
                  setEditEntityRecord={setEditEntityRecord}
                  setAddEntityRecord={setAddEntityRecord}
                  setIsViewClicked={setIsViewClicked}
                  setIsDuplicateModalOpen={setIsDuplicateModalOpen}
                  setSelectedRow={setSelectedRow}
                  setValue={setValue}
                  setIsAdvancedSearch={setIsAdvancedSearch}
                  setRefetchKey={setRefetchKey}
                  setViewDuplicateRecord={setViewDuplicateRecord}
                  fetchTableDataCallback={fetchTableDataCallback}
                  fetchViewLayout={fetchViewLayout}
                  fetchEditRecordData={fetchEditRecordData}
                />
              )}
            </Modal>
          </div>
        </AddDataModal>
      )}
      {isDynamicQueue &&
        (hasMultiProduct ||
          workflowDefinition?.global_variables?.multiIntake) &&
        (showPatientWarningPopup || showFacilityPopup) && (
          <Modal
            isOpen={showPatientWarningPopup || showFacilityPopup}
            closeModal={() => {
              if (showPatientWarningPopup) setShowPatientWarningPopup(false)
              if (showFacilityPopup) setShowFacilityPopup(false)
            }}
            showCloseButton
            width="min-w-[30rem] max-w-[30rem]"
          >
            <div className="flex flex-col p-[0.87rem] w-[28.625rem] gap-5">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col items-center gap-5">
                  <div className="flex items-center justify-center size-[3.6rem] rounded-full bg-error-100">
                    <SvgWarning />
                  </div>
                  <span className="text-base font-medium">
                    {showPatientWarningPopup
                      ? NAMESPACES.ACTION_REQUIRED
                      : NAMESPACES.UNRESOLVED_FACILITY_POPUP_HEADER}
                  </span>
                  <div className="text-xs leading-snug text-center">
                    {showPatientWarningPopup
                      ? isPatientMinor
                        ? MINOR_PATIENT_POPUP_MESSAGE
                        : ADULT_PATIENT_POPUP_MESSAGE
                      : UNRESOLVED_FACILITY_POPUP_MESSAGE}
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        )}
      <ControlledDropDown
        searchLength={element.keyName === NAMESPACES.STATE ? 2 : 3}
        name={fieldKeyName as Path<T>}
        isCrossable
        icon={renderDropdownIcon}
        tabIndex={rest?.tabIndex}
        errors={errors}
        label={element.meta.label}
        control={control}
        isSearchable={!lookupVal?.id}
        isCustomSearch
        onSearchChange={(sv) => {
          setSearchValue(sv)
          if (!element.meta?.filter && !lookupVal?.id) {
            fetchSearchedOptions(sv)
          }
        }}
        isBordered={!matchResult}
        highlightClassName={highlightClass}
        onChange={(e) => {
          if (
            e?.label === '' &&
            e?.value === '' &&
            isEmpty(element?.meta?.filter)
          ) {
            setSearchValue('')
            fetchSearchedOptions('')
          }
          setValue(fieldKeyName as Path<T>, e.value as PathValue<T, Path<T>>)
          const clearKeys: string[] = []
          if (fullLayout?.layout_data.meta?.form_rules) {
            const hasTrigger = fullLayout?.layout_data.meta?.form_rules.filter(
              (rule: FormRuleType) => {
                const when = rule.when as
                  | { trigger?: string; watch_key_names?: string[] }
                  | undefined
                return (
                  when?.trigger === 'onChange' &&
                  when.watch_key_names?.includes(behaviorRuleKey)
                )
              }
            )
            if (hasTrigger.length > 0) {
              hasTrigger.forEach((rule: FormRuleType) => {
                const clearKeyNames = rule.then?.clear_key_names as string[]
                if (clearKeyNames) {
                  clearKeys.push(...clearKeyNames)
                }
              })
              if (layoutUid) {
                triggerFormBehaviorEvaluation(
                  layoutUid,
                  'change',
                  behaviorRuleKey
                )
              }
            }
          }
          const valueToPass =
            typeof e?.value === 'string' ? { id: e.value } : e?.value
          changeContextValue(valueToPass, clearKeys)
        }}
        options={options}
        // disabled={lookupVal?.id}
        // options={entityData.map((data: { name: string; id: number }) => {
        //   return {
        //     label: data?.name,
        //     value: data?.id.toString(),
        //   }
        // })}
        {...rest}
      />
    </div>
  )
}

export default LookupRender

const ShowEditLayout = ({
  lookupVal,
  element,
  setIsCreateNew,
  setRefetchOptions,
  setRefetchSelectedId,
  refetchOptions,
  refetchSelectedId,
  editLayout,
  //TODO: Might be required later
  // toggleCreateNew,
  handleSubmit,
}: ShowEditLayoutPropsType) => {
  return lookupVal?.id ? (
    <AddEditRecordPage
      mode="edit"
      entityId={element.meta.relationship?.target_entity_id.toString() ?? ''}
      id={lookupVal.id}
      loaderVisible={false}
      allowNavigate={false}
      layoutId={editLayout?.id}
      onSaveClick={() => {
        setRefetchSelectedId(!refetchSelectedId)
        setRefetchOptions(!refetchOptions)
        setIsCreateNew((prev) => {
          return {
            ...prev,
            isOpen: false,
            isCreate: null,
          }
        })
      }}
      shouldModalOpen={false}
      onCancel={() => {
        setIsCreateNew((prev) => ({
          ...prev,
          isOpen: false,
          isCreate: null,
        }))
      }}
      // showFooter={true}
    />
  ) : (
    <FormRenderer
      heading={
        <div className="flex justify-between pb-2">
          <div className="flex items-center justify-between gap-4">
            <span className="font-medium text-base">
              Add{' '}
              {element?.meta?.label || element.meta.relationship?.child_label}
            </span>
            {/* TODO: Might be reqiured later */}
            {/* <div className="flex items-center gap-3">
              <div className="cursor-pointer">
                <SvgCross onClick={toggleCreateNew} />
              </div>
            </div> */}
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="flex items-center gap-3">
              <div className="flex gap-3">
                <CustomButton
                  onClick={() =>
                    setIsCreateNew((prev) => ({
                      ...prev,
                      isOpen: false,
                      isCreate: null,
                    }))
                  }
                >
                  <span>{NAMESPACES.CANCEL_BUTTON}</span>
                </CustomButton>
                <CustomButton isFilled type="submit">
                  <span>{NAMESPACES.SAVE_BUTTON}</span>
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      }
      fullLayout={editLayout as EntityLayout}
      layout={editLayout?.layout_data as LayoutData}
      mode="add"
      handleFormSubmit={handleSubmit}
    />
  )
}

const ShowNoLayoutSection = ({
  setIsCreateNew,
  isLoading,
}: ShowNoLayoutSectionPropsType) => {
  if (isLoading) return null
  return (
    <div className="flex relative justify-center items-center h-full m-auto font-primary text-center  min-h-[5rem]">
      <div
        className="absolute top-0 right-0 cursor-pointer"
        onClick={() => {
          setIsCreateNew((prev) => ({
            ...prev,
            isOpen: false,
            isCreate: null,
          }))
        }}
        tabIndex={TAB_INDEX_DEFAULT}
        onKeyDown={(e) => {
          if (e?.key === ShortcutKeys.Enter || e?.key === ShortcutKeys.Space) {
            e?.preventDefault()
            setIsCreateNew((prev) => ({
              ...prev,
              isOpen: false,
              isCreate: null,
            }))
          }
        }}
      >
        <SvgCross />
      </div>
      <div className="font-semibold">{NAMESPACES.NO_LAYOUT}</div>
    </div>
  )
}
